/**
 * 69.17 step 2: the flight model — one step for every craft a pilot flies, its numbers a row per craft
 * (constants/flightModel.ts). The owner's picks (DECISIONS 236): semi-physical (B2), the controls direct (E2), the lifters
 * the physics (D1). A tick, per craft:
 *
 * 1. **The controls become wishes.** The pilot's direct rates (and, for the dropship, its nacelles' swing and its lift)
 *    pass a small stability augmentation: a rate let go holds the attitude (the dropship levels), a pull past the wing's
 *    attack or the load limit is held there (the fighter's limiter).
 * 2. **The wishes become the lifters' settings** (the dropship's four nacelles: a thrust and a tilt each, differential
 *    thrust for roll and pitch, differential tilt for yaw; the fighter's throttle, nozzle vector and lift jets), which move
 *    toward them at their own rates (a fan spools, a nacelle swings). A lifter lost (`lost`) gives nothing, so the craft
 *    flies lame by itself.
 * 3. **The physics.** The lifters' actual thrust, the wing's lift and drag by its attack and speed, the hull's drag, the
 *    seen gravity: forces and moments integrated (semi-implicit, PHYS_DT).
 *
 * Pure, deterministic in dmath; world units in and out (the dials are in people: × `person`). The ground (`env.ground`)
 * is the caller's: a contact is clamped and reported, the landing's rule is step 3's.
 */
import type { CraftBody, CraftControls, Vec3 } from '../types';
import { DROPSHIP_FLIGHT, FIGHTER_FLIGHT, FLIGHT_GRAVITY } from '../constants/flightModel';
import { dcos, dsin, dasin, dacos, datan2 } from './dmath';
import { vecAdd, vecScale, vecSub, vecDot, vecCross, vecLength, vecNormalize } from '../math';

/** Where the craft flies: the planet's centre (its gravity's), and the ground's radius under a direction (world), if any. */
export interface FlightEnv {
  centre: Vec3;
  ground?: (dir: Vec3) => number;
}

/** What else a step can say: the craft touched the ground at `speed` (people a second into it). */
export interface FlightStep { body: CraftBody; touch?: { speed: number } }

const clamp = (x: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, x));
const left = (b: Pick<CraftBody, 'front' | 'up'>) => vecCross(b.up, b.front);
/** A body-frame vector (x left, y up, z front) in the world. */
function toWorld(b: Pick<CraftBody, 'front' | 'up'>, v: Vec3): Vec3 {
  return vecAdd(vecAdd(vecScale(left(b), v.x), vecScale(b.up, v.y)), vecScale(b.front, v.z));
}
/** A world vector in the body frame. */
function toBody(b: Pick<CraftBody, 'front' | 'up'>, v: Vec3): Vec3 {
  return { x: vecDot(v, left(b)), y: vecDot(v, b.up), z: vecDot(v, b.front) };
}

/** A craft at rest or in flight: at `pos` facing `front` (made level to the planet's up there), moving at `vel`. */
export function newCraft(kind: CraftBody['kind'], pos: Vec3, front: Vec3, vel: Vec3, env: FlightEnv, landed = false): CraftBody {
  const upW = vecNormalize(vecSub(pos, env.centre));
  const f = vecNormalize(vecSub(front, vecScale(upW, vecDot(front, upW))));
  const n = kind === 'dropship' ? DROPSHIP_FLIGHT.nacelles.length : 3;
  return { kind, pos, vel, front: f, up: upW, rate: { x: 0, y: 0, z: 0 }, thrust: new Array(n).fill(0), tilt: new Array(n).fill(0), ...(landed ? { landed: true as const } : {}) };
}

/** One step of `dt` seconds under the pilot's controls (`lost`: the lifters gone, SHIP_PARTS' nacelle order for the dropship). */
export function stepCraft(b: CraftBody, c: CraftControls, env: FlightEnv, dt: number, person: number, lost: readonly boolean[] = []): FlightStep {
  const upW = vecNormalize(vecSub(b.pos, env.centre));
  const r = b.kind === 'dropship' ? dropshipForces(b, c, upW, env, dt, person, lost) : fighterForces(b, c, upW, env, dt, person);
  // the physics: semi-implicit — the rates, then the attitude by them, the velocity, then the place
  const rate = vecAdd(b.rate, vecScale(r.spin, dt));
  const w = toWorld(b, rate), wl = vecLength(w);
  let front = b.front, up = b.up;
  if (wl > 1e-12) {
    const axis = vecScale(w, 1 / wl), ang = wl * dt;
    front = rotate(front, axis, ang); up = rotate(up, axis, ang);
  }
  front = vecNormalize(front);
  up = vecNormalize(vecSub(up, vecScale(front, vecDot(up, front))));
  const accel = vecAdd(r.accel, vecScale(upW, -FLIGHT_GRAVITY * person));
  let vel = vecAdd(b.vel, vecScale(accel, dt));
  let pos = vecAdd(b.pos, vecScale(vel, dt));
  let landed = b.landed === true && r.liftOff === false;
  let touch: FlightStep['touch'];
  if (env.ground) {
    const d = vecSub(pos, env.centre), dist = vecLength(d), dir = vecScale(d, 1 / dist);
    const floor = env.ground(dir) + (b.kind === 'dropship' ? DROPSHIP_FLIGHT.gear : FIGHTER_FLIGHT.gear) * person;
    if (dist < floor) {
      const into = -vecDot(vel, dir);
      if (into > 0) touch = { speed: into / person };
      pos = vecAdd(env.centre, vecScale(dir, floor));
      vel = vecScale(vecSub(vel, vecScale(dir, vecDot(vel, dir))), Math.max(0, 1 - 4 * dt));   // (the ground holds it; its roll-out slows it)
      landed = true;
    } else if (dist > floor + 0.05 * person) landed = false;
  }
  return { body: { kind: b.kind, pos, vel, front, up, rate: landed ? { x: 0, y: 0, z: 0 } : rate, thrust: r.thrust, tilt: r.tilt, ...(landed ? { landed: true as const } : {}), ...(r.hold !== undefined && !landed ? { hold: r.hold } : {}) }, ...(touch ? { touch } : {}) };
}

/** `v` turned about the unit `axis` by `ang` radians (Rodrigues). */
function rotate(v: Vec3, axis: Vec3, ang: number): Vec3 {
  const c = dcos(ang), s = dsin(ang);
  return vecAdd(vecAdd(vecScale(v, c), vecScale(vecCross(axis, v), s)), vecScale(axis, vecDot(axis, v) * (1 - c)));
}

interface Forces { accel: Vec3; spin: Vec3; thrust: number[]; tilt: number[]; liftOff: boolean; /** the height (radius) the dropship holds, let go of the lift */ hold?: number }

/** The attitude's rate wished for an axis: the stick's rate, held within `max` of level; let go, back toward level. */
function attitudeRate(stick: number, angle: number, rate: number, max: number, level: number): number {
  if (Math.abs(stick) > 0.02) {
    const want = stick * rate;
    return (want > 0 && angle >= max) || (want < 0 && angle <= -max) ? 0 : want;
  }
  return -level * angle;
}

/**
 * The dropship: the stick's rates and the nacelles' swing (W / S) and lift (Space / C) — the lift trimmed to hold its
 * height against the seen gravity through its present attitude — allocated over the four nacelles, which move toward it.
 */
function dropshipForces(b: CraftBody, c: CraftControls, upW: Vec3, env: FlightEnv, dt: number, person: number, lost: readonly boolean[]): Forces {
  const D = DROPSHIP_FLIGHT, L = left(b), [Ix, Iy, Iz] = D.inertia;
  const pitch = dasin(clamp(vecDot(b.front, upW), -1, 1)), bank = dasin(clamp(vecDot(L, upW), -1, 1));   // (nose up +; right wing down +)
  const vlevel = vecLength(vecSub(b.vel, vecScale(upW, vecDot(b.vel, upW)))) / person, radius = vecLength(vecSub(b.pos, env.centre));
  // 1. the wishes: the rates (body axes: a nose up is −x, a yaw right −y, a roll right +z) and the rate loop's moments.
  // 2b (the owner: "requires a lot of control inputs simultaneously"): A / D a coordinated turn — the yaw asked and the bank
  // its speed needs to carry the path round with it (tan φ = v ω / g); the mouse's x a yaw of its own; Q / E a bank alone,
  // the slide; the bank held where it is asked, let go back to level (the momentum the slide carries is the physics')
  // (A / D's yaw falls with the speed — quick in the hover, wide at cruise, where the bank it can lean carries the path round —
  // a little past what the bank holds, so a hard turn slides: the momentum carries, the owner's "slide")
  const turn = c.turn ?? 0, yawRate = clamp(turn * (D.turnSpeed / (D.turnSpeed + vlevel)) + (c.yaw ?? 0), -1, 1) * D.rates.yaw;
  const bankWant = clamp(datan2(vlevel * turn * D.rates.yaw, FLIGHT_GRAVITY) + (c.strafe ?? 0) * D.maxBank, -D.maxBank, D.maxBank);
  const want = {
    x: -attitudeRate(c.pitch ?? 0, pitch, D.rates.pitch, D.maxPitch, D.level),
    y: -yawRate,
    z: clamp(D.level * (bankWant - bank), -D.rates.roll, D.rates.roll),
  };
  // (each axis its own gain: the yaw's, worked by the nacelles' swing — the slowest of their moves — gentle, so it doesn't chase
  // their lag round and round; 2b, the owner: "the nacelles wiggle and fight eachother a bit on turns")
  const mx = Ix * D.sas.pitch * (want.x - b.rate.x), my = Iy * D.sas.yaw * (want.y - b.rate.y), mz = Iz * D.sas.roll * (want.z - b.rate.z);
  // the nacelles' common swing (W forward, S aft), and the lift through the present common tilt
  const fwd = c.forward ?? 0, lift = c.lift ?? 0;
  const n = D.nacelles.length, alive = D.nacelles.reduce((k, _, i) => k + (lost[i] ? 0 : 1), 0);
  let tiltNow = 0;
  for (let i = 0; i < n; i++) tiltNow += b.tilt[i] ?? 0;
  tiltNow /= n;
  const c0 = dcos(tiltNow), s0 = dsin(tiltNow);
  const vz = vecDot(b.vel, upW) / person;
  // the lift: Space / C ask a vertical speed (up to `climb` either way); let go, the height is held (2b, the owner: "doesn't
  // hold altitude while controlling forward or turning") — the radius it was let go at, a critically damped hold, the
  // planet's curve (v² / r) fed forward, so one input does one thing; it acts on the height only, never on the drift
  let hold: number | undefined;
  let aV: number;
  if (Math.abs(lift) > 0.02 || b.landed) aV = FLIGHT_GRAVITY + D.altHold * (lift * D.climb - vz);
  else {
    hold = b.hold ?? radius;
    aV = FLIGHT_GRAVITY - (vlevel * vlevel) / (radius / person) + D.hold.k * ((hold - radius) / person) - D.hold.d * vz;
  }
  const along = vecDot(vecAdd(vecScale(b.up, c0), vecScale(b.front, s0)), upW);
  const total = Math.max(0, aV) / Math.max(0.25, along);
  const liftOff = !b.landed || aV > FLIGHT_GRAVITY + 0.5;
  // 2. the allocation: the common thrust, a left-right difference for the roll, front-rear for the pitch, a tilt difference for the yaw
  const X = Math.abs(D.nacelles[0][0]), cs = Math.max(0.2, c0);
  // (the roll and the yaw together — 2b: leaning, the thrusts' left-right difference both rolls and yaws it, the swing's difference
  // both yaws and rolls it, and solved apart the two fought; together, about the common tilt θ, thrust difference a and swing
  // difference δ give roll 4X(a cos θ − T₀ δ sin θ) and yaw −4X(a sin θ + T₀ δ cos θ), so a = cos θ·m_roll − sin θ·m_yaw and
  // δ = −(sin θ·m_roll + cos θ·m_yaw) / T₀ — in the hover the roll by thrust and the yaw by swing, as before)
  const mRoll = mz / (4 * X), mYaw = my / (4 * X), T0 = Math.max(0.1, total / n);
  const delta = clamp(-(s0 * mRoll + c0 * mYaw) / T0, -D.yawTilt, D.yawTilt);
  // (the swing no further than the thrust left can carry and still hold the lift — a pilot can't tip it out of the sky by W —
  // and short of it by the yaw's difference, so W and a turn held together blend: it slows a little into the turn, neither wins)
  const room = dacos(clamp(Math.max(FLIGHT_GRAVITY, aV) / Math.max(1e-6, 0.9 * alive * D.thrustMax), 0, 1)) - Math.abs(delta);   // (a tenth kept for the attitude)
  const tiltWant = fwd >= 0 ? Math.min(fwd * D.tiltRange[1], room) : -fwd * D.tiltRange[0];
  const thrust: number[] = [], tilt: number[] = [];
  const force = { x: 0, y: 0, z: 0 }, moment = { x: 0, y: 0, z: 0 };
  // 2. the allocation over the nacelles that work: the nearest to an even share that gives the lift, the roll and the pitch
  // asked (a least-squares solve on their present tilts: three wishes, three or four nacelles); a nacelle lost, the others
  // carry it as they can
  const asked = allocate(D.nacelles, b.tilt, lost, total * c0, mz, mx, alive);   // (the whole roll: the allocation reads the swing's difference as it stands, and its thrust difference comes out a)
  // the attitude first: past a nacelle's full thrust the common share gives way, the differences kept (it sinks level, not nose first)
  const over = Math.max(0, ...asked.map((t, i) => (lost[i] ? 0 : t - D.thrustMax)));
  for (let i = 0; i < n; i++) {
    const [x, y, z] = D.nacelles[i], side = x > 0 ? 1 : -1;
    const T = clamp(asked[i] - over, 0, D.thrustMax) / D.thrustMax;
    const th = clamp(tiltWant + side * delta, D.tiltRange[0], D.tiltRange[1]);
    // 3. the lifters move toward it: the fan spools, the nacelle swings at its rate; a lost one gives nothing
    const share = lost[i] ? 0 : (b.thrust[i] ?? 0) + (T - (b.thrust[i] ?? 0)) * Math.min(1, dt / D.spool);
    const t0 = b.tilt[i] ?? 0, swung = t0 + clamp(th - t0, -D.tiltRate * dt, D.tiltRate * dt);
    thrust.push(share); tilt.push(swung);
    // the physics: this nacelle's force (along its axis: up tilted toward the front) and its moment about the centre of mass
    const F = share * D.thrustMax, fy = F * dcos(swung), fz = F * dsin(swung);
    force.y += fy; force.z += fz;
    moment.x += y * fz - z * fy; moment.y += -x * fz; moment.z += x * fy;
  }
  // the hull's drag, in its own frame
  const vb = vecScale(toBody(b, b.vel), 1 / person), q = D.drag.quad;
  const drag = { x: -q[0] * vb.x * Math.abs(vb.x) - D.drag.lin * vb.x, y: -q[1] * vb.y * Math.abs(vb.y) - D.drag.lin * vb.y, z: -q[2] * vb.z * Math.abs(vb.z) - D.drag.lin * vb.z };
  const accel = vecScale(toWorld(b, vecAdd(force, drag)), person);
  const spin = { x: moment.x / Ix - D.angDamp * b.rate.x, y: moment.y / Iy - D.angDamp * b.rate.y, z: moment.z / Iz - D.angDamp * b.rate.z };
  return { accel, spin, thrust, tilt, liftOff, hold };
}

/**
 * The nacelles' thrusts (each a share of the craft's people a second²; a lost one 0) nearest an even share of `lift` that
 * give the body-up force `lift`, the roll moment `roll` and the pitch moment `pitch` (body axes) on their present tilts:
 * T = T̄ + Aᵀ(AAᵀ)⁻¹(w − AT̄), A's rows each nacelle's share of the three. Singular (fewer than three working): the even share.
 */
function allocate(at: ReadonlyArray<readonly [number, number, number]>, tilts: readonly number[], lost: readonly boolean[], lift: number, roll: number, pitch: number, alive: number): number[] {
  const n = at.length, even = alive > 0 ? lift / alive : 0;
  const A: number[][] = [[], [], []];
  for (let i = 0; i < n; i++) {
    const [x, y, z] = at[i], c = dcos(tilts[i] ?? 0), s = dsin(tilts[i] ?? 0), on = lost[i] ? 0 : 1;
    A[0].push(on * c); A[1].push(on * x * c); A[2].push(on * (y * s - z * c));
  }
  const T = at.map((_, i) => (lost[i] ? 0 : even));
  const w = [lift, roll, pitch];
  const sol = solveRows(A, w.map((v, k) => v - A[k].reduce((sum, a, i) => sum + a * T[i], 0)));
  return sol ? T.map((t, i) => (lost[i] ? 0 : t + A[0][i] * sol[0] + A[1][i] * sol[1] + A[2][i] * sol[2])) : T;
}

/** (AAᵀ)λ = r for the rows of A (Gaussian elimination, partial pivoting, a fixed order: the same each run); null when singular. */
function solveRows(A: number[][], r: number[]): number[] | null {
  const k = A.length, M = A.map((ra, i) => [...A.map(rb => ra.reduce((sum, a, j) => sum + a * rb[j], 0)), r[i]]);
  for (let c = 0; c < k; c++) {
    let p = c;
    for (let i = c + 1; i < k; i++) if (Math.abs(M[i][c]) > Math.abs(M[p][c])) p = i;
    if (Math.abs(M[p][c]) < 1e-9) return null;
    [M[c], M[p]] = [M[p], M[c]];
    for (let i = 0; i < k; i++) {
      if (i === c) continue;
      const f = M[i][c] / M[c][c];
      for (let j = c; j <= k; j++) M[i][j] -= f * M[c][j];
    }
  }
  return M.map((row, i) => row[k] / row[i]);
}

/**
 * The fighter: the nozzles' thrust by the throttle, the wing's lift by its attack and speed (stalling past `alphaMax`), its
 * drag (the zero-lift and the induced), the slip's side force, the nose weathervaning toward the flight path; the stick's
 * rates through a rate loop whose authority is the surfaces' by the speed, the nozzles' by the throttle and the reaction
 * jets'; a pull held at the attack the load limit allows. The lift jets (`jets`) lift it slow.
 */
function fighterForces(b: CraftBody, c: CraftControls, upW: Vec3, env: FlightEnv, dt: number, person: number): Forces {
  const F = FIGHTER_FLIGHT;
  const vb = vecScale(toBody(b, b.vel), 1 / person), V = vecLength(vb);
  const alpha = V > 1 ? datan2(-vb.y, vb.z) : 0, beta = V > 1 ? datan2(vb.x, vb.z) : 0;
  const aLim = V > 1 ? Math.min(F.alphaMax, (F.nMax * FLIGHT_GRAVITY) / (F.liftK * V * V)) : F.alphaMax;
  // 1. the wishes: the stick's rates, the pull held at the limit's attack (a push the same, the other way)
  let pull = (c.pitch ?? 0) * F.rates.pitch;
  if (pull > 0) pull *= clamp((aLim - alpha) / 0.05, 0, 1);
  else if (pull < 0) pull *= clamp((aLim + alpha) / 0.05, 0, 1);
  // (an axis let go holds the rate that follows the planet's curve — the local horizon turns at the level speed over the radius —
  // not a still nose, which at speed would climb away from the ground)
  const vlevel = vecSub(b.vel, vecScale(upW, vecDot(b.vel, upW))), curve = toBody(b, vecScale(vecCross(upW, vlevel), 1 / vecLength(vecSub(b.pos, env.centre))));
  const free = (stick: number | undefined) => Math.abs(stick ?? 0) <= 0.02;
  const want = { x: free(c.pitch) ? curve.x : -pull, y: free(c.yaw) ? curve.y : -(c.yaw ?? 0) * F.rates.yaw, z: (c.roll ?? 0) * F.rates.roll };
  const sasX = free(c.pitch) ? F.sas * 0.1 : F.sas;   // (the stick let go: the trim flies the nose)
  const q = Math.min(1, (V / F.corner) * (V / F.corner));
  // 2. the lifters: the throttle spools, the nozzles vector with the pitch (drawn), the lift jets on or off
  const throttle = (b.thrust[0] ?? 0) + (clamp(c.throttle ?? 0, 0, 1) - (b.thrust[0] ?? 0)) * Math.min(1, dt / F.spool);
  const jets = c.jets ? 1 : 0;
  const vector = clamp(c.pitch ?? 0, -1, 1) * F.vector;
  const auth = F.auth.aero * q + F.auth.vector * throttle + F.auth.rcs + (jets ? F.jets.rcs : 0);
  // (the nose weathervanes toward the attack that keeps it level: hands off, it follows the planet's curve)
  let trim = 0;
  if (V > 1) {
    const vh = vecNormalize(b.vel), across = vecSub(b.up, vecScale(vh, vecDot(b.up, vh))), al = vecLength(across);
    // (the lift that keeps the path on the planet's curve: the seen gravity less what the curve itself asks, V² / r — past the
    // planet's orbital speed, √(g r), ~81 people a second on an 1,100-person world, it is a push: the trim goes negative)
    const r = vecLength(vecSub(b.pos, env.centre)), vlev = vecLength(vecSub(b.vel, vecScale(upW, vecDot(b.vel, upW)))) / person;
    if (al > 1e-9) trim = clamp(((FLIGHT_GRAVITY - (vlev * vlev) / (r / person)) * vecDot(upW, vecScale(across, 1 / al))) / (F.liftK * V * V), -F.alphaMax, F.alphaMax);
  }
  const spin = {
    x: clamp(sasX * (want.x - b.rate.x), -auth, auth) + F.stability.alpha * (alpha - trim) * q,
    y: clamp(F.sas * (want.y - b.rate.y), -auth, auth) + F.stability.beta * beta * q,
    z: clamp(F.sas * (want.z - b.rate.z), -auth, auth),
  };
  // 3. the forces (people a second², the world's): thrust along the nose, the wing's lift across the path, drag along it, the slip's side force
  let accel = vecScale(b.front, throttle * F.thrust * person);
  if (jets) {   // (as the dropship's lift: trimmed to hold the height through its attitude, Space / C up and down)
    const lift = c.lift ?? 0, vz = vecDot(b.vel, upW) / person;
    const aV = FLIGHT_GRAVITY + F.jets.altHold * (lift * F.jets.climb - vz);
    accel = vecAdd(accel, vecScale(b.up, (clamp(aV, 0, F.jets.thrust) / Math.max(0.25, vecDot(b.up, upW))) * person));
  }
  if (V > 1) {
    const vhat = vecNormalize(b.vel);
    const a = Math.abs(alpha), cl = (a <= F.alphaMax ? F.liftK * a : F.liftK * F.alphaMax * Math.max(0.2, 1 - (a - F.alphaMax) / F.alphaMax)) * Math.sign(alpha);
    const across = vecSub(b.up, vecScale(vhat, vecDot(b.up, vhat))), al = vecLength(across);
    if (al > 1e-9) accel = vecAdd(accel, vecScale(across, (cl * V * V * person) / al));
    accel = vecAdd(accel, vecScale(vhat, -(F.cd0 + F.induced * cl * cl) * V * V * person));
    accel = vecAdd(accel, vecScale(left(b), -F.side * beta * V * V * person));
  }
  return { accel, spin, thrust: [throttle, throttle, jets], tilt: [vector, vector, 0], liftOff: !b.landed || throttle * F.thrust > FLIGHT_GRAVITY || jets === 1 };
}
